/**
 * Single source of truth for the Teamwork Graph entity types the connections
 * byline treats as incoming links, and the field each one carries its URL in.
 *
 * These are members of the `GraphStoreCypherQueryV2AriNodeUnion` returned by
 * `useIncomingLinksQuery`. The union has no shared interface, so the query must
 * select a `... on <Type>` fragment per member; this catalog is the authoritative
 * list of which members to select and with which field. The
 * `incomingLinkTypes-test` drift-guard asserts the compiled query and this catalog
 * stay in lockstep.
 *
 * Every selection is aliased `<Type>_<field>` (see {@link getIncomingLinkUrlAlias}).
 * That is a requirement, not a style choice: GraphQL's same-response-shape rule
 * rejects one response name `url` carrying `String` (Townsquare, all `External*`)
 * and `URL` (Mercury, Bitbucket, DevOps) across sibling fragments, and `links`
 * resolves to a different object type on every Confluence type.
 *
 * Scope is deliberately limited to content-like entities. People/org peers
 * (workers, teams, customers, organisations, positions) are intentionally omitted
 * so they don't surface as "links to this page", as are types the schema gives no
 * URL at all (sprints, statuses, issue types, worklogs, groups).
 */
export const INCOMING_LINK_URL_FIELDS = {
	// Confluence — `links.webUi` is site-relative and needs `links.base` prepended.
	ConfluenceBlogPost: 'links',
	ConfluenceDatabase: 'links',
	ConfluenceEmbed: 'links',
	ConfluenceFolder: 'links',
	ConfluenceFooterComment: 'links',
	ConfluenceInlineComment: 'links',
	ConfluencePage: 'links',
	ConfluenceSlide: 'links',
	ConfluenceSpace: 'links',
	ConfluenceWhiteboard: 'links',
	// Jira
	JiraIssue: 'webUrl',
	JiraPlatformComment: 'webUrl',
	JiraProject: 'webUrl',
	JiraServiceManagementComment: 'webUrl',
	// Atlas (townsquare)
	TownsquareComment: 'url',
	TownsquareGoal: 'url',
	TownsquareGoalUpdate: 'url',
	TownsquareProject: 'url',
	TownsquareProjectUpdate: 'url',
	// Loom
	LoomComment: 'url',
	LoomSpace: 'url',
	LoomVideo: 'url',
	// Focus (mercury)
	MercuryAsk: 'url',
	MercuryFocusArea: 'url',
	// Bitbucket and the DevOps graph
	BitbucketRepository: 'href',
	DeploymentSummary: 'url',
	DevOpsDesign: 'url',
	DevOpsDocument: 'url',
	DevOpsOperationsIncidentDetails: 'url',
	DevOpsOperationsPostIncidentReviewDetails: 'url',
	DevOpsProjectDetails: 'url',
	DevOpsPullRequestDetails: 'url',
	DevOpsRepository: 'url',
	DevOpsSecurityVulnerabilityDetails: 'url',
	// Compass
	CompassComponent: 'url',
	// Third-party ("External") peers
	ExternalBranch: 'url',
	ExternalBuildInfo: 'url',
	ExternalCalendarEvent: 'url',
	ExternalComment: 'url',
	ExternalCommit: 'url',
	ExternalConversation: 'url',
	ExternalDashboard: 'url',
	ExternalDataTable: 'url',
	ExternalDeal: 'url',
	ExternalDeployment: 'url',
	ExternalDesign: 'url',
	ExternalDocument: 'url',
	ExternalMessage: 'url',
	ExternalPullRequest: 'url',
	ExternalRemoteLink: 'url',
	ExternalRepository: 'url',
	ExternalSoftwareService: 'url',
	ExternalSpace: 'url',
	ExternalTest: 'url',
	ExternalVideo: 'url',
	ExternalVulnerability: 'url',
	ExternalWorkItem: 'url',
} as const;

/** `__typename` of a curated incoming link entity. */
export type IncomingLinkTypename = keyof typeof INCOMING_LINK_URL_FIELDS;

/** The curated `__typename`s the byline query selects a fragment for. */
export const INCOMING_LINK_TYPENAMES = Object.keys(
	INCOMING_LINK_URL_FIELDS,
) as IncomingLinkTypename[];

/** Narrows an arbitrary `__typename` to one of the curated incoming link types. */
export const isCuratedIncomingLinkTypename = (
	typename: string | null | undefined,
): typename is IncomingLinkTypename => typename != null && typename in INCOMING_LINK_URL_FIELDS;

/**
 * The response name the query selects a type's URL field under. Both the query
 * text and the reader derive from this, so a type can never be selected under one
 * alias and read under another.
 */
export const getIncomingLinkUrlAlias = (typename: IncomingLinkTypename): string =>
	`${typename}_${INCOMING_LINK_URL_FIELDS[typename]}`;
